import { createReducer } from "@reduxjs/toolkit";
import { AdminActionCreator } from "./actions";

const initialState = {
  uploadFiles: {
    progress: 0,
    pending: false,
    error: false,
    errorMessage: null as string | null,
    paths: [] as string[],
  },
  deleteUser: {
    pending: false,
    error: false,
    errorMessage: null as string | null,
    userId: null as number | string | null,
  },
  subscribedUsers: {
    pending: false,
    error: false,
    errorMessage: null as string | null,
    users: [] as any[],
    total: null as number | null,
    page: 1,
    perPage: 100,
    lastPage: null as number | null,
    nextUrl: null as string | null,
    prevUrl: null as string | null,
  },
  users: {
    pending: false,
    error: false,
    errorMessage: null as string | null,

    users: [] as any[],
    total: null as number | null,

    page: 1,
    perPage: 50,
    lastPage: null as number | null,
    nextUrl: null as string | null,
    prevUrl: null as string | null,

    query: { search: "", sort: null as string | null, order: null as "asc" | "desc" | null },
  },
  setUserSubscription: {
    pending: false,
    error: false,
    errorMessage: null as string | null,

    userId: null as number | string | null,
    planId: null as number | string | null,
    planCode: null as string | null,
    expiresAt: null as string | null,
  },
  cancelUserSubscription: {
    pending: false,
    error: false,
    errorMessage: null as string | null,
    userId: null as number | string | null,
  },
  updateUser: {
    pending: false,
    error: false,
    errorMessage: null as string | null,
    userId: null as number | string | null,
  },
  resetUserPassword: {
    pending: false,
    error: false,
    errorMessage: null as string | null,
    email: null as string | null,
  },
};

export const adminReducer = createReducer(initialState, (builder) => {
  builder
      // -------- uploadFiles --------
      .addCase(AdminActionCreator.uploadFiles.pending, (state) => {
        state.uploadFiles.pending = true;
        state.uploadFiles.error = false;
        state.uploadFiles.errorMessage = null;
        state.uploadFiles.paths = [];
      })
      .addCase(AdminActionCreator.uploadFiles.fulfilled, (state, action) => {
        state.uploadFiles.pending = false;
        state.uploadFiles.error = false;
        state.uploadFiles.errorMessage = null;
        state.uploadFiles.paths = action.payload?.path ?? [];
      })
      .addCase(AdminActionCreator.uploadFiles.rejected, (state, action: any) => {
        state.uploadFiles.pending = false;
        state.uploadFiles.error = true;
        state.uploadFiles.errorMessage =
            action?.payload?.message || action?.error?.message || "Unknown error";
      })

      // -------- deleteUser --------
      .addCase(AdminActionCreator.deleteUser.pending, (state, action: any) => {
        state.deleteUser.pending = true;
        state.deleteUser.error = false;
        state.deleteUser.errorMessage = null;
        state.deleteUser.userId = action?.meta?.arg?.userId ?? null;
      })
      .addCase(AdminActionCreator.deleteUser.fulfilled, (state, action: any) => {
        state.deleteUser.pending = false;
        state.deleteUser.error = false;
        state.deleteUser.errorMessage = null;

        const deletedId = action?.payload?.userId ?? state.deleteUser.userId;

        if (deletedId != null) {
          const beforeUsers = state.users.users.length;
          state.users.users = state.users.users.filter(
              (u: any) => String(u?.id) !== String(deletedId),
          );
          const removedFromUsers = beforeUsers - state.users.users.length;

          const beforeSubs = state.subscribedUsers.users.length;
          state.subscribedUsers.users = state.subscribedUsers.users.filter(
              (u: any) => String(u?.id) !== String(deletedId),
          );
          const removedFromSubs = beforeSubs - state.subscribedUsers.users.length;

          if (typeof state.users.total === "number" && removedFromUsers > 0) {
            state.users.total = Math.max(0, state.users.total - removedFromUsers);
          }
          if (typeof state.subscribedUsers.total === "number" && removedFromSubs > 0) {
            state.subscribedUsers.total = Math.max(
                0,
                state.subscribedUsers.total - removedFromSubs,
            );
          }
        }

        state.deleteUser.userId = null;
      })
      .addCase(AdminActionCreator.deleteUser.rejected, (state, action: any) => {
        state.deleteUser.pending = false;
        state.deleteUser.error = true;
        state.deleteUser.errorMessage =
            action?.payload?.message || action?.error?.message || "Unknown error";
      })

      // -------- updateUser --------
      .addCase(AdminActionCreator.updateUser.pending, (state, action: any) => {
        state.updateUser.pending = true;
        state.updateUser.error = false;
        state.updateUser.errorMessage = null;
        state.updateUser.userId = action?.meta?.arg?.userId ?? null;
      })
      .addCase(AdminActionCreator.updateUser.fulfilled, (state, action: any) => {
        state.updateUser.pending = false;
        state.updateUser.error = false;
        state.updateUser.errorMessage = null;

        const userId =
            action?.meta?.arg?.userId ?? action?.payload?.userId ?? state.updateUser.userId;

        // ✅ что реально отправляли (уже очищенное в thunk)
        const sent = action?.meta?.arg?.payload ?? {};

        // ✅ если бэк вернул юзера — берём его как source of truth
        const returnedUser = action?.payload?.user ?? null;

        const mergeUser = (u: any) => {
          if (!u) return u;
          // приоритет: returnedUser > sent > текущий u
          // но subscription и другие вложенные штуки не затираем, если их нет в returnedUser
          const base = { ...u, ...(sent || {}) };
          const next = returnedUser ? { ...base, ...returnedUser } : base;

          // ✅ защищаем subscription от случайного затирания
          if (u?.subscription && !returnedUser?.subscription) {
            next.subscription = u.subscription;
          }

          return next;
        };

        if (userId != null) {
          state.users.users = state.users.users.map((u: any) =>
              String(u?.id) === String(userId) ? mergeUser(u) : u,
          );

          state.subscribedUsers.users = state.subscribedUsers.users.map((u: any) =>
              String(u?.id) === String(userId) ? mergeUser(u) : u,
          );
        }

        state.updateUser.userId = null;
      })
      .addCase(AdminActionCreator.updateUser.rejected, (state, action: any) => {
        state.updateUser.pending = false;
        state.updateUser.error = true;
        state.updateUser.errorMessage =
            action?.payload?.message || action?.error?.message || "Unknown error";
      })
      // -------- resetUserPassword --------
      .addCase(AdminActionCreator.resetUserPassword.pending, (state, action: any) => {
        state.resetUserPassword.pending = true;
        state.resetUserPassword.error = false;
        state.resetUserPassword.errorMessage = null;

        state.resetUserPassword.email = action?.meta?.arg?.email ?? null;
      })
      .addCase(AdminActionCreator.resetUserPassword.fulfilled, (state, action: any) => {
        state.resetUserPassword.pending = false;
        state.resetUserPassword.error = false;
        state.resetUserPassword.errorMessage = null;

        // можно оставить email (чтобы показать "Отправлено на ..."),
        // а можно очистить — я очищу, как и в остальных экшенах
        state.resetUserPassword.email = null;
      })
      .addCase(AdminActionCreator.resetUserPassword.rejected, (state, action: any) => {
        state.resetUserPassword.pending = false;
        state.resetUserPassword.error = true;
        state.resetUserPassword.errorMessage =
            action?.payload?.message || action?.error?.message || "Unknown error";
      })

      // -------- setUserSubscription --------
      .addCase(AdminActionCreator.setUserSubscription.pending, (state, action: any) => {
        state.setUserSubscription.pending = true;
        state.setUserSubscription.error = false;
        state.setUserSubscription.errorMessage = null;

        state.setUserSubscription.userId = action?.meta?.arg?.userId ?? null;
        state.setUserSubscription.planId = action?.meta?.arg?.planId ?? null;
        state.setUserSubscription.planCode = action?.meta?.arg?.planCode ?? null;
        state.setUserSubscription.expiresAt = action?.meta?.arg?.expiresAt ?? null;
      })
      .addCase(AdminActionCreator.setUserSubscription.fulfilled, (state, action: any) => {
        state.setUserSubscription.pending = false;
        state.setUserSubscription.error = false;
        state.setUserSubscription.errorMessage = null;

        const userId = action?.meta?.arg?.userId ?? action?.payload?.userId ?? null;
        const planCode =
            action?.meta?.arg?.planCode ?? action?.payload?.planCode ?? null;
        const expiresAt =
            action?.meta?.arg?.expiresAt ?? action?.payload?.expiresAt ?? null;

        const patchUser = (u: any) => {
          if (!u) return u;
          const next = { ...u };
          next.subscription = {
            ...(u?.subscription ?? {}),
            ...(planCode ? { plan: planCode } : {}),
            ...(expiresAt ? { expires_at: expiresAt } : {}),
          };
          return next;
        };

        if (userId != null) {
          state.users.users = state.users.users.map((u: any) =>
              String(u?.id) === String(userId) ? patchUser(u) : u,
          );

          state.subscribedUsers.users = state.subscribedUsers.users.map((u: any) =>
              String(u?.id) === String(userId) ? patchUser(u) : u,
          );
        }

        state.setUserSubscription.userId = null;
        state.setUserSubscription.planId = null;
        state.setUserSubscription.planCode = null;
        state.setUserSubscription.expiresAt = null;
      })
      .addCase(AdminActionCreator.setUserSubscription.rejected, (state, action: any) => {
        state.setUserSubscription.pending = false;
        state.setUserSubscription.error = true;
        state.setUserSubscription.errorMessage =
            action?.payload?.message || action?.error?.message || "Unknown error";
      })

      // -------- cancelUserSubscription --------
      .addCase(AdminActionCreator.cancelUserSubscription.pending, (state, action: any) => {
        state.cancelUserSubscription.pending = true;
        state.cancelUserSubscription.error = false;
        state.cancelUserSubscription.errorMessage = null;

        state.cancelUserSubscription.userId = action?.meta?.arg?.userId ?? null;
      })
      .addCase(AdminActionCreator.cancelUserSubscription.fulfilled, (state, action: any) => {
        state.cancelUserSubscription.pending = false;
        state.cancelUserSubscription.error = false;
        state.cancelUserSubscription.errorMessage = null;

        const userId = action?.meta?.arg?.userId ?? action?.payload?.userId ?? null;
        if (userId == null) {
          state.cancelUserSubscription.userId = null;
          return;
        }
        const yesterday = (() => {
          const d = new Date();
          d.setDate(d.getDate() - 1);
          const pad2 = (n: number) => String(n).padStart(2, "0");
          return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
        })();

        const patchToDefault = (u: any) => {
          if (!u) return u;
          return {
            ...u,
            subscription: {
              ...(u?.subscription ?? {}),
              plan: "default",
              expires_at: yesterday,
            },
          };
        };

        // users list: patch user
        state.users.users = state.users.users.map((u: any) =>
            String(u?.id) === String(userId) ? patchToDefault(u) : u,
        );

        // subscribed list: remove user
        const beforeSubs = state.subscribedUsers.users.length;
        state.subscribedUsers.users = state.subscribedUsers.users.filter(
            (u: any) => String(u?.id) !== String(userId),
        );
        const removedFromSubs = beforeSubs - state.subscribedUsers.users.length;

        if (typeof state.subscribedUsers.total === "number" && removedFromSubs > 0) {
          state.subscribedUsers.total = Math.max(
              0,
              state.subscribedUsers.total - removedFromSubs,
          );
        }

        state.cancelUserSubscription.userId = null;
      })
      .addCase(AdminActionCreator.cancelUserSubscription.rejected, (state, action: any) => {
        state.cancelUserSubscription.pending = false;
        state.cancelUserSubscription.error = true;
        state.cancelUserSubscription.errorMessage =
            action?.payload?.message || action?.error?.message || "Unknown error";
      })

      // -------- subscribedUsers --------
      .addCase(AdminActionCreator.getSubscribedUsers.pending, (state) => {
        state.subscribedUsers.pending = true;
        state.subscribedUsers.error = false;
        state.subscribedUsers.errorMessage = null;
      })
      .addCase(AdminActionCreator.getSubscribedUsers.fulfilled, (state, action) => {
        state.subscribedUsers.pending = false;
        state.subscribedUsers.error = false;
        state.subscribedUsers.errorMessage = null;

        const nextUsers = action.payload?.users ?? [];
        const requestedPage = Number(action.meta?.arg?.page ?? 1);
        const shouldAppend = requestedPage > 1 && Number(action.payload?.page ?? requestedPage) > 1;
        state.subscribedUsers.users = shouldAppend
            ? [...state.subscribedUsers.users, ...nextUsers.filter((user: any) => !state.subscribedUsers.users.some((current: any) => String(current?.id) === String(user?.id)))]
            : nextUsers;
        state.subscribedUsers.total = action.payload?.total ?? null;

        state.subscribedUsers.page = action.payload?.page ?? state.subscribedUsers.page;
        state.subscribedUsers.perPage =
            action.payload?.perPage ?? state.subscribedUsers.perPage;
        state.subscribedUsers.lastPage =
            action.payload?.lastPage ?? state.subscribedUsers.lastPage;

        state.subscribedUsers.nextUrl = action.payload?.nextUrl ?? null;
        state.subscribedUsers.prevUrl = action.payload?.prevUrl ?? null;
      })
      .addCase(AdminActionCreator.getSubscribedUsers.rejected, (state, action: any) => {
        state.subscribedUsers.pending = false;
        state.subscribedUsers.error = true;
        state.subscribedUsers.errorMessage =
            action?.payload?.message || action?.error?.message || "Unknown error";
      })

      // -------- users --------
      .addCase(AdminActionCreator.getUsers.pending, (state) => {
        state.users.pending = true;
        state.users.error = false;
        state.users.errorMessage = null;
      })
      .addCase(AdminActionCreator.getUsers.fulfilled, (state, action) => {
        state.users.pending = false;
        state.users.error = false;

        const nextUsers = action.payload?.users ?? [];
        const requestedPage = Number(action.meta?.arg?.page ?? 1);
        const shouldAppend = requestedPage > 1 && Number(action.payload?.page ?? requestedPage) > 1;
        state.users.users = shouldAppend
            ? [...state.users.users, ...nextUsers.filter((user: any) => !state.users.users.some((current: any) => String(current?.id) === String(user?.id)))]
            : nextUsers;
        state.users.total = action.payload?.total ?? null;

        state.users.page = action.payload?.page ?? 1;
        state.users.perPage = action.payload?.perPage ?? 50;
        state.users.lastPage = action.payload?.lastPage ?? null;

        state.users.nextUrl = action.payload?.nextUrl ?? null;
        state.users.prevUrl = action.payload?.prevUrl ?? null;

        const q = action.payload?.query ?? {};
        state.users.query = {
          search: q.search ?? "",
          sort: q.sort ?? null,
          order: q.order ?? null,
        };
      })
      .addCase(AdminActionCreator.getUsers.rejected, (state, action: any) => {
        state.users.pending = false;
        state.users.error = true;
        state.users.errorMessage =
            action?.payload?.message || action?.error?.message || "Unknown error";
      });
});
